import { Fragment, ReactElement, useEffect, useState } from "react";
import { CircularProgress, Paper, useMediaQuery } from "@mui/material";
import { IIndicator, IIndicatorData } from "../../../../types";
import {
  IAxisBand,
  IAxisLinear,
  IBarChartConfig,
  IPoint,
  ISeriesWithAxes
} from "../../../../components/charts/types";
import { useChartDimensions, useUniqueId } from "../../../../components/charts/chartUtils";
import Chart from "../../../../components/charts/chart";
import HtmlTooltip from "../../../../components/charts/tooltip";
import { formatNumber } from "../../../../system/utils";
import * as d3 from "d3";
import { useFetch } from "../../../../system/useFetch";

interface IProps {
  dataContext: IIndicator;
}

const tenMinutes = 600000;
const barMargin = 2;
const paddingInner = 0.1;
const paddingOuter = 0.1;
const align = 0.5;

const BarChartIndicator = ({ dataContext }: IProps): ReactElement => {
  const matches400 = useMediaQuery("(min-width:600px)");
  const { data } = useFetch<IIndicatorData>({ module: "system", query: "getIndicatorDataContext", payload: { nodeID: dataContext.nodeID, nodeType: dataContext.nodeType, nodeIndicatorID: dataContext.nodeIndicatorID } }, tenMinutes);
  const [ chartsConfig, setChartsConfig ] = useState<IBarChartConfig | null>(null);
  const barID = useUniqueId("bar-chart-ind");
  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    width: matches400 ? 180 : 110,
    marginBottom: matches400 ? 10 : 6,
    marginLeft: 0,
    marginRight: 25,
    marginTop: 0
  });

  useEffect(() => {
    if (data) {
      if (data) {
        const series: ISeriesWithAxes[] = [];
        const categoriesList: string[] = [];
        let maximum = 0;

        data.properties.forEach((row) => {
          let item = series.find(x => x.name === row.name);
          if (!item) {
            item = {
              name: row.name,
              xAxisName: "X",
              yAxisName: "Y",
              value: [],
              color: row.color ?? "gray",
              unit: row.unit
            };

            series.push(item);
          }

          const value = parseFloat(row.value);
          maximum = Math.max(maximum, value);

          (item.value as IPoint[]).push({ x: row.title ?? "---", y: value });

          if (!categoriesList.find(x => x === row.title ? x : "---")) {
            categoriesList.push(row.title ?? "---");
          }
        });

        setChartsConfig({
          title: data.title,
          xAxis: {
            name: "X",
            hidden: true,
            ticksAngle: 45,
            categories: categoriesList
          },
          yAxis: [{
            name: "Y",
            minimum: 0,
            maximum,
            ticksCount: 2,
            hidden: true,
            formatTick: (value) => {
              return typeof value === "number" ? (value as number).toFixed(2) : (value as IPoint).y.toFixed(2);
            }
          }],
          series
        });
      }
    }
  }, [data, setChartsConfig]);

  if (!chartsConfig) {
    return (
      <Paper className="menu-indicator-lg display-flex-row justify-content-center align-items-center"  >
        <CircularProgress className=""/>
      </Paper>
    );
  }

  const xAxis = chartsConfig.xAxis as IAxisBand;
  const yAxis = chartsConfig.yAxis[0] as IAxisLinear;

  const xScale = d3.scaleBand()
    .domain(xAxis.categories ?? [])
    .range([0, dimensions.boundedWidth])
    .paddingInner(paddingInner)
    .paddingOuter(paddingOuter)
    .align(align);

  const yScale = d3.scaleLinear()
    .domain([yAxis.maximum, yAxis.minimum])
    .range([0, dimensions.boundedHeight]);

  const width = xScale.bandwidth() / chartsConfig.series.length - barMargin;
  const marginInner = paddingInner * (width - 20) / 2.0;

  const categories: any = {};

  const catLen = xAxis.categories.length;
  const fx = dimensions.boundedWidth / catLen;
  const fo = fx / 2;


  return(
    <Paper className="menu-indicator-lg display-flex-row p-1" >
      <div ref={ref} >
        <Chart dimensions={dimensions} >

          <g className="Axis AxisHorizontal" transform={`translate(${dimensions.boundedWidth - 5}, 0)`}>
            <line className="Axis__line" y2={dimensions.boundedHeight}/>
            <text fill="#fff"  x={2} y={yScale(yAxis.minimum)}
              textAnchor="start" fontSize="0.6em">
              {formatNumber(yAxis.minimum, 1)}
            </text>
            <text fill="#fff" x={2} y={yScale(yAxis.maximum)}
              textAnchor="start" fontSize="0.6em" dominantBaseline="hanging">
              {formatNumber(yAxis.maximum, 1)}
            </text>
          </g>

          <text
            transform={`rotate(-90) translate(-${dimensions.boundedHeight / 1.3}, 4)`}
            textAnchor="middle"
            fill="#fff" fontSize="0.8em" >{chartsConfig.title}</text>

          <g>
            {chartsConfig && xAxis.categories.map((category, index) => {
              const x = xScale(category) ?? 0;

              return (
                <text key={`${barID}-cat-${index}`} x={x + fo}
                  textAnchor="middle"
                  y={dimensions.boundedHeight + dimensions.marginBottom + 2}
                  fill="#fff" fontSize="0.7em" >{category}</text>
              );
            })

            }
          </g>
          <g>
            {chartsConfig && chartsConfig.series.map((series, index) => {

              return (
                <g key={`${barID}-${index}`}>
                  {(series.value as IPoint[]).map((value, valueIndex) => {
                    const categoryName = value.x as string;
                    const y = yScale(value.y) ?? 0;
                    const x = xScale(categoryName) ?? 0;
                    const height = dimensions.boundedHeight - y;

                    if (categories[categoryName] === undefined) {
                      categories[categoryName] = height;
                    } else {
                      categories[categoryName] += height;
                    }

                    return (
                      <Fragment key={`${barID}-${index}-${valueIndex}`}>
                        <HtmlTooltip title={
                          <div>
                            <div className="display-flex-row align-items-center">
                              <div style={{ background: series.color, width: 10, height: 10, marginRight: 10 }}/>
                              <strong className="flex-grow-1">
                                {series.name}
                              </strong>
                              <strong className="flex-grow-0">
                                {formatNumber(value.y, 1)}
                                {series.unit && <sub>[{series.unit}]</sub>}
                              </strong>
                            </div>
                          </div>
                        }>
                          <g>
                            <rect
                              className="Bars__rect"
                              fill={series.color}
                              height={height}
                              width={width}
                              y={y}
                              x={x + (width + barMargin) * index + barMargin + marginInner}
                            />
                          </g>
                        </HtmlTooltip>
                      </Fragment>
                    );
                  })}
                </g>
              );
            })}
          </g>
        </Chart>
      </div>
    </Paper>
  );
};

export default BarChartIndicator;
